import { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useLocation } from 'wouter';
import { API_BASE } from '../lib/api';
import { Gift, Calendar, Mail, User, CreditCard, ArrowLeft, ArrowRight, CheckCircle, Loader2 } from 'lucide-react';
import AppLayout from '../components/AppLayout';

interface GiftPlan {
  duration: number;
  months: number;
  price: number;
  displayName: string;
  savings: string;
}

interface GiftFormData {
  planDuration: number;
  recipientEmail: string;
  recipientName: string;
  personalMessage: string;
  deliveryDate: string;
  purchaserName: string;
  purchaserEmail: string;
}

const GiftPurchasePage = () => {
  const { getToken, isSignedIn } = useAuth();
  const [, navigate] = useLocation();

  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [plans, setPlans] = useState<GiftPlan[]>([]);
  const [purchasedVoucher, setPurchasedVoucher] = useState<any>(null);
  const [formData, setFormData] = useState<GiftFormData>({
    planDuration: 12, // Default to 12-month annual plan
    recipientEmail: '',
    recipientName: '',
    personalMessage: 'Hi, I\'m delighted to gift you HiPo AI Coach because I truly believe in your potential. Whether you\'re navigating a new role, balancing work-life, or just need a listening ear, these 5+1 specialized modules are here to support you. I hope this becomes a game-changer in achieving the effortless high performance and success you\'re after!',
    deliveryDate: '',
    purchaserName: '',
    purchaserEmail: ''
  });

  // Load gift plans on mount
  useEffect(() => {
    const loadPlans = async () => {
      try {
        const token = await getToken();
        const response = await fetch(`${API_BASE}/gift-purchase/plans`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        const data = await response.json();
        if (data.success) {
          setPlans(data.plans);
          if (data.plans.length > 0) {
            setFormData(prev => ({ ...prev, planDuration: data.plans[0].duration }));
          }
        }
      } catch (error) {
        console.error('Failed to load gift plans:', error);
      }
    };

    if (isSignedIn) {
      loadPlans();
    }
  }, [isSignedIn, getToken]);

  // Update user info when available (simplified for now)
  useEffect(() => {
    // TODO: Get user info from Clerk auth when available
    setFormData(prev => ({
      ...prev,
      purchaserName: prev.purchaserName || 'Gift Purchaser',
      purchaserEmail: prev.purchaserEmail || 'purchaser@example.com'
    }));
  }, []);

  const selectedPlan = plans.find(p => p.duration === formData.planDuration);

  const handleNextStep = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handlePurchase = async () => {
    setIsLoading(true);
    try {
      const token = await getToken();
      const response = await fetch(`${API_BASE}/gift-purchase`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        // 1. Open Razorpay Checkout for the gift
        const options = {
          key: data.nextStep.razorpayKeyId,
          amount: data.nextStep.amount * 100, // Razorpay expects paise
          currency: "INR",
          name: "HiPo AI Coach",
          description: data.nextStep.description,
          image: "https://hipoaicoach.com/hipo-logo.png",
          order_id: data.nextStep.razorpayOrderId,
          handler: async (response: any) => {
            try {
              // 2. Verify gift payment on backend
              const confirmResponse = await fetch(`${API_BASE}/gift-purchase/confirm`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  voucherId: data.voucher.id,
                  paymentId: response.razorpay_payment_id,
                  paymentStatus: 'success'
                }),
              });

              const confirmData = await confirmResponse.json();
              if (confirmData.success) {
                alert('Gift purchased successfully! The voucher has been sent.');
                navigate('/settings');
              }
            } catch (err) {
              console.error('Verification failed:', err);
              alert('Payment verification failed. Please contact support.');
            }
          },
          prefill: {
            name: formData.purchaserName,
            email: formData.purchaserEmail,
          },
          theme: {
            color: "#3B82F6",
          },
        };

        const rzp = new (window as any).Razorpay(options);
        rzp.open();
      } else {
        console.error('Failed to create gift:', data.message);
      }
    } catch (error) {
      console.error('Error purchasing gift:', error);
    }
    setIsLoading(false);
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3, 4].map((step) => (
        <div key={step} className="flex items-center">
          <div className={`
          w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold
          ${currentStep === step
              ? 'bg-royal-blue text-white'
              : currentStep > step
                ? 'bg-green-500 text-white'
                : 'bg-slate-200 text-slate-600'
            }
        `}>
            {currentStep > step ? <CheckCircle className="h-4 w-4" /> : step}
          </div>
          {step < 4 && (
            <div className={`w-12 h-0.5 mx-2 ${currentStep > step ? 'bg-green-500' : 'bg-slate-200'}`} />
          )}
        </div>
      ))}
    </div>
  );

  const renderPlanSelection = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-display font-bold text-slate-900 mb-2">Annual Gift Subscription</h2>
        <p className="text-slate-600">Give a full year of personalized AI coaching</p>
      </div>

      <div className="max-w-md mx-auto">
        <div className="rounded-2xl border-2 border-royal-blue bg-royal-blue/5 ring-2 ring-royal-blue/20 p-8">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold mb-4">
              <Gift className="h-4 w-4" />
              Annual Subscription
            </div>
            <h3 className="text-2xl font-display font-bold text-slate-900 mb-2">12-Month Coaching Journey</h3>
            <div className="text-4xl font-bold text-royal-blue mb-2">₹999</div>
            <p className="text-slate-600 mb-4">₹83 per month • Full year of growth</p>

            <div className="bg-white rounded-xl p-4 space-y-2 text-sm text-left">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>Access to all 6 AI coaches</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>7 monthly coaching conversations, high impact coaching support</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>Minutes of Meeting after each session</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>Personal growth insights & analytics</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>Action ledger & goal tracking</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderRecipientInfo = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-display font-bold text-slate-900 mb-2">Recipient Details</h2>
        <p className="text-slate-600">Who is this gift for?</p>
      </div>

      <div className="space-y-4 max-w-md mx-auto">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            <User className="h-4 w-4 inline mr-1" />
            Recipient Name
          </label>
          <input
            type="text"
            value={formData.recipientName}
            onChange={(e) => setFormData(prev => ({ ...prev, recipientName: e.target.value }))}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-royal-blue focus:border-transparent"
            placeholder="Enter recipient's full name"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            <Mail className="h-4 w-4 inline mr-1" />
            Recipient Email
          </label>
          <input
            type="email"
            value={formData.recipientEmail}
            onChange={(e) => setFormData(prev => ({ ...prev, recipientEmail: e.target.value }))}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-royal-blue focus:border-transparent"
            placeholder="recipient@example.com"
            required
          />
        </div>

        <div>
          <label htmlFor="delivery-date" className="block text-sm font-medium text-slate-700 mb-2">
            <Calendar className="h-4 w-4 inline mr-1" />
            Delivery Date (Optional)
          </label>
          <input
            id="delivery-date"
            type="date"
            value={formData.deliveryDate}
            onChange={(e) => setFormData(prev => ({ ...prev, deliveryDate: e.target.value }))}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-royal-blue focus:border-transparent"
            min={new Date().toISOString().split('T')[0]}
            placeholder="Select delivery date"
            title="Choose when to deliver the gift subscription"
          />
          <p className="text-xs text-slate-500 mt-1">Leave empty to send immediately</p>
        </div>
      </div>
    </div>
  );

  const renderPersonalization = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-display font-bold text-slate-900 mb-2">Personal Message</h2>
        <p className="text-slate-600">Add a heartfelt note to your gift</p>
      </div>

      <div className="max-w-md mx-auto">
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Personal Message
        </label>
        <textarea
          value={formData.personalMessage}
          onChange={(e) => setFormData(prev => ({ ...prev, personalMessage: e.target.value.slice(0, 280) }))}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-royal-blue focus:border-transparent"
          rows={4}
          placeholder={`I thought you'd love exploring HiPoAiCoach's personalized coaching experience! This ${selectedPlan?.months}-month subscription will help you unlock your potential with AI-powered coaching insights.`}
          maxLength={280}
          aria-label="Personal message for gift recipient"
        />
        <p className="text-xs text-slate-500 mt-1">{formData.personalMessage.length}/280 characters</p>
      </div>
    </div>
  );

  const renderReview = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-display font-bold text-slate-900 mb-2">Review Your Gift</h2>
        <p className="text-slate-600">Confirm the details before purchase</p>
      </div>

      <div className="max-w-md mx-auto bg-slate-50 rounded-2xl p-6 space-y-4">
        <div className="flex justify-between">
          <span className="text-slate-600">Plan:</span>
          <span className="font-semibold">{selectedPlan?.displayName}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-600">Price:</span>
          <span className="font-semibold text-royal-blue">₹{selectedPlan?.price}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-600">Recipient:</span>
          <span className="font-semibold">{formData.recipientName}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-600">Email:</span>
          <span className="font-semibold">{formData.recipientEmail}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-600">Delivery:</span>
          <span className="font-semibold">
            {formData.deliveryDate ? new Date(formData.deliveryDate).toLocaleDateString() : 'Immediately'}
          </span>
        </div>
        {formData.personalMessage && (
          <div>
            <span className="text-slate-600 block mb-1">Message:</span>
            <p className="text-sm bg-white p-3 rounded-lg">{formData.personalMessage}</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderPlanSelection();
      case 2:
        return renderRecipientInfo();
      case 3:
        return renderPersonalization();
      case 4:
        return renderReview();
      default:
        return renderPlanSelection();
    }
  };

  if (!isSignedIn) {
    return (
      <AppLayout currentPage="home">
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-display font-bold text-slate-900 mb-4">Sign In Required</h1>
            <p className="text-slate-600">Please sign in to purchase a gift subscription.</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout currentPage="home">
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white py-8">
        <div className="max-w-2xl mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-royal-blue/10 text-royal-blue px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <Gift className="h-4 w-4" />
              Gift a Coaching Journey
            </div>
            <h1 className="text-3xl font-display font-bold text-slate-900">Give the Gift of Growth</h1>
            <p className="text-slate-600 mt-2">Help someone unlock their potential with personalized AI coaching</p>
          </div>

          {/* Step Indicator */}
          {renderStepIndicator()}

          {/* Step Content */}
          <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-8 mb-8">
            {renderStepContent()}
          </div>

          {/* Navigation */}
          <div className="flex justify-between">
            <button
              onClick={handlePrevStep}
              disabled={currentStep === 1}
              className="flex items-center gap-2 px-6 py-3 text-slate-600 hover:text-slate-900 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowLeft className="h-4 w-4" />
              Previous
            </button>

            {currentStep < 4 ? (
              <button
                onClick={handleNextStep}
                disabled={
                  (currentStep === 2 && (!formData.recipientName || !formData.recipientEmail))
                }
                className="flex items-center gap-2 px-6 py-3 bg-royal-blue text-white rounded-lg hover:bg-royal-blue/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
                <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                onClick={handlePurchase}
                disabled={isLoading}
                className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <CreditCard className="h-4 w-4" />
                )}
                Complete Purchase
              </button>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default GiftPurchasePage;
